import {
	EntityViewModel,
	getTypeForEntity,
	IAccountTag,
	IContact,
	IDictionary,
	IOperationResultNoValue,
	ResourceAutoCompleteViewModelType,
	TagViewModel,
	IAutomationTemplate,
} from '@ViewModels';
import { invalidateAllContactAutomationsQueries } from '../../../../../queries/Automation/queries/useContactAutomationsQuery';
import { css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { inject, Observer, observer } from 'mobx-react';
import React from 'react';
import Measure, { ContentRect } from 'react-measure';
import { integrationSources } from '../../../../../extViewmodels/Utils';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { IEventLoggingComponentProps, useEventLogging, withEventLogging } from '../../../../../models/Logging';
import { PersistentStorageManager } from '../../../../../models/Storage';
import { contactIsFromEnabledIntegration } from '../../../../../models/UiUtils';
import { useAutomationTemplateQuery } from '../../../../../queries/Automation/queries/useAutomationTemplateQuery';
import { useStartAutomationForContactMutation } from '../../../../../queries/Automation/useStartAutomationForContact';
import {
	ContactViewModel,
	ICreateAutomationRequest,
	IntegrationSources,
	ResourceAutoCompleteViewModel,
	TagAlertViewModel,
	TagsViewModel,
} from '../../../../../viewmodels/AppViewModels';
import { brandPrimary } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { AddTagButton } from '../../../tags/AddTagButton';
import { Tag } from '../../../tags/Tag';
import { InlineValueEditor } from '../../InlineValueEditor';
import { TagSearchesStorageKey } from './hooks';
import { AddTagInput } from './presentation';
import { styleSheet } from './styles';
import './styles.less';

const MAX_TAGS = 6;

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps, IErrorMessageComponentProps {
	disabled?: boolean;
	entity?: EntityViewModel;
	tagType: ResourceAutoCompleteViewModelType.AccountTag | ResourceAutoCompleteViewModelType.Tag;
	onRequestAddTag(tag: IAccountTag): void;
	compactLayout?: boolean;
	isCompanyTags?: boolean;
	onPendingTagToSetHasAutomationTemplateId?: (automationTemplateId: IAccountTag) => void;
	showInputEditor?: boolean;
}
interface IState {
	disabled?: boolean;
	editingPendingTag?: boolean;
	highlightedAutoCompleteSuggestionIndex?: number;
	isAutocompleteOpen?: boolean;
	pendingTag?: string;
	popularTags?: string[];
	tagAlerts?: IDictionary<TagAlertViewModel>;
	tagsListMountedContentRect?: ContentRect;
}

class _TagsEditor extends React.Component<IProps, IState> {
	private mMounted = false;
	private mAutoCompleteVm: ResourceAutoCompleteViewModel<string | IAccountTag>;
	private tagsVm: TagsViewModel;

	private recentSearches: (string | IAccountTag)[] = [];

	public state: IState = {
		isAutocompleteOpen: false,
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_TagsEditor, 'suggestions'>(this, {
			suggestions: computed,
		});
	}

	public UNSAFE_componentWillMount() {
		const { userSession, logEvent, tagType, isCompanyTags = false, showInputEditor } = this.props;

		this.mAutoCompleteVm = new ResourceAutoCompleteViewModel<string | IAccountTag>(userSession, {
			type: tagType,
			pageSize: 25,
		});

		this.tagsVm = new TagsViewModel(userSession);

		// get recommended tags
		const onComplete = (tags: string[]) => {
			if (this.mMounted) {
				this.setState({
					popularTags: tags,
				});
			}
		};
		const onError = (action: string) => (error: IOperationResultNoValue) => {
			logEvent(action, { ...error });
		};
		if (isCompanyTags) {
			const promise = this.tagsVm.getTags({ sort: 'desc', sortBy: 'Stats.Total' }, MAX_TAGS);
			if (promise) {
				logEvent('LoadPopularTags');
				promise
					.then((opResult: any) => {
						onComplete((opResult.values || []).map((x: any) => x.tag));
					})
					.catch(onError('LoadPopularTags-Error'));
			}
		} else {
			const promise = this.tagsVm.getSuggestedTagsForContactsV2(false, 25);
			if (promise) {
				logEvent('LoadSuggestedTags');
				promise
					.then((tags: IAccountTag[]) => {
						onComplete((tags || []).map((x: IAccountTag) => x.name) as string[]);
					})
					.catch(onError('LoadSuggestedTags-Error'));
			}
		}

		const nextState = this.getNextStateWithProps(this.props);
		// Initialize editingPendingTag based on showInputEditor prop on mount
		if (showInputEditor !== undefined) {
			this.setState({
				...nextState,
				editingPendingTag: showInputEditor,
			});
		} else if (nextState) {
			this.setState(nextState);
		}
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		const nextState = this.getNextStateWithProps(nextProps);
		// Only sync editingPendingTag when showInputEditor prop actually changes
		if (nextProps.showInputEditor !== undefined && nextProps.showInputEditor !== this.props.showInputEditor) {
			this.setState({
				editingPendingTag: nextProps.showInputEditor,
			});
		}
		if (nextState) {
			this.setState(nextState);
		}
	}

	public componentDidMount() {
		this.mMounted = true;
	}

	public componentWillUnmount() {
		this.mMounted = false;
	}

	public render() {
		const { editingPendingTag, pendingTag, disabled } = this.state;
		const { compactLayout } = this.props;

		return (
			<footer className={css(styleSheet.tagListItem, styleSheet.tagListItemFooter)} key='footer'>
				{!!editingPendingTag && (
					<div>
						<InlineValueEditor
							compactLayout={compactLayout}
							autoComplete='off'
							autoFocus={true}
							placeholder='Search Tags'
							disabled={disabled}
							saveButtonDisabled={disabled || this.suggestions.length === 0}
							id='tags-editor-input'
							onBlur={this.onInputBlur}
							onCancelButtonClicked={this.onClickCancelAddTag}
							onChange={this.onPendingTagChange}
							onFocus={this.onInputFocus}
							onKeyDown={this.onInputKeyDown}
							onRenderInput={this.onRenderAddTagInput}
							onSaveButtonClicked={() => this.addTag({ name: pendingTag })}
							value={pendingTag || ''}
							hideCancelButton={this.props.showInputEditor || false}
						/>
					</div>
				)}
				{!editingPendingTag && <AddTagButton disabled={disabled} onClick={() => this.addTag({ name: pendingTag })} />}
			</footer>
		);
	}

	private get suggestions() {
		const { pendingTag, popularTags } = this.state;
		const suggestions = pendingTag
			? this.mAutoCompleteVm.searchResults || []
			: !!popularTags && popularTags.length > 0
				? popularTags.slice(0, 3)
				: [];
		return suggestions;
	}

	private onRenderAddTagInput = (input: JSX.Element) => {
		return (
			<>
				<Observer>
					{() => {
						const { isAutocompleteOpen, highlightedAutoCompleteSuggestionIndex, pendingTag, popularTags } = this.state;
						const storedContactsQueryStringValue = PersistentStorageManager.local.get(TagSearchesStorageKey);
						this.recentSearches = storedContactsQueryStringValue ? JSON.parse(storedContactsQueryStringValue) : [];
						const suggestions = pendingTag
							? this.mAutoCompleteVm.searchResults || []
							: !!popularTags && popularTags.length > 0
								? popularTags.slice(0, 3)
								: [];
						return (
							<AddTagInput
								input={input}
								pendingTag={pendingTag}
								highlightedAutoCompleteSuggestionIndex={highlightedAutoCompleteSuggestionIndex}
								isAutocompleteOpen={isAutocompleteOpen}
								isBusy={!!this.mAutoCompleteVm.isBusy}
								suggestions={suggestions}
								searchResults={this.mAutoCompleteVm.searchResults}
								recentSearches={this.recentSearches}
								addTag={this.addTag}
							/>
						);
					}}
				</Observer>
			</>
		);
	};

	private getNextStateWithProps = (nextProps: IProps) => {
		const nextState: IState = {};
		if (nextProps.disabled !== this.state.disabled) {
			nextState.disabled = nextProps.disabled;
		}

		if (Object.keys(nextState).length > 0) {
			return nextState;
		}
	};

	private onClickCancelAddTag = () => {
		if (this.state.editingPendingTag) {
			this.setState({
				editingPendingTag: false,
				pendingTag: '',
			});
		}
	};

	private onInputBlur = () => {
		this.setState(
			{
				highlightedAutoCompleteSuggestionIndex: -1,
				isAutocompleteOpen: false,
			},
			() => {
				this.mAutoCompleteVm.reset();
			}
		);
	};

	private onInputFocus = () => {
		this.setState(
			{
				editingPendingTag: true,
				highlightedAutoCompleteSuggestionIndex: -1,
				isAutocompleteOpen: true,
			},
			() => {
				const { pendingTag } = this.state;
				if (pendingTag) {
					this.mAutoCompleteVm.setSearchQuery(pendingTag);
				}
			}
		);
	};

	private onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const { highlightedAutoCompleteSuggestionIndex, pendingTag } = this.state;
		switch (e.key) {
			case 'Enter': {
				if (highlightedAutoCompleteSuggestionIndex > -1) {
					let selectedSuggestion = this.suggestions[highlightedAutoCompleteSuggestionIndex];
					if (!this.state.pendingTag) {
						selectedSuggestion =
							highlightedAutoCompleteSuggestionIndex < 3
								? this.state.popularTags[highlightedAutoCompleteSuggestionIndex]
								: this.recentSearches[highlightedAutoCompleteSuggestionIndex - this.suggestions.length];
					}
					const accountTagToAdd =
						typeof selectedSuggestion === 'string' ? { name: selectedSuggestion } : selectedSuggestion;
					this.addTag(accountTagToAdd);
				} else {
					this.addTag({ name: pendingTag });
				}
				break;
			}
			case 'ArrowUp': {
				const index = highlightedAutoCompleteSuggestionIndex - 1;
				if (index >= -1) {
					this.setState({
						highlightedAutoCompleteSuggestionIndex: index,
					});
				}
				break;
			}
			case 'ArrowDown': {
				const index = highlightedAutoCompleteSuggestionIndex + 1;
				if (!this.state.pendingTag) {
					if (index < this.suggestions.length + this.recentSearches.length) {
						this.setState({
							highlightedAutoCompleteSuggestionIndex: index,
						});
					}
				} else {
					if (index < this.suggestions.length) {
						this.setState({
							highlightedAutoCompleteSuggestionIndex: index,
						});
					}
				}

				break;
			}
			default: {
				break;
			}
		}
	};

	private addTag = (pendingTag: IAccountTag) => {
		const { editingPendingTag } = this.state;

		if (!editingPendingTag) {
			this.setState({
				editingPendingTag: true,
			});
			return;
		}

		if (pendingTag.name) {
			const { onRequestAddTag } = this.props;
			const indexOfMatchingRecentSearch = this.recentSearches.indexOf(pendingTag.name);
			// tag not in recent searches array
			if (indexOfMatchingRecentSearch < 0) {
				this.recentSearches.unshift(pendingTag.name);
				if (this.recentSearches.length > 10) {
					this.recentSearches.pop();
				}
			} else {
				this.recentSearches.unshift(this.recentSearches.splice(indexOfMatchingRecentSearch, 1)[0]);
			}
			PersistentStorageManager.local.setObject(TagSearchesStorageKey, this.recentSearches);
			const nextState: IState = {
				editingPendingTag: false,
				isAutocompleteOpen: false,
				pendingTag: '',
				highlightedAutoCompleteSuggestionIndex: -1,
			};

			const onFinish = (pendingTagToSet: IAccountTag) => {
				const { onPendingTagToSetHasAutomationTemplateId } = this.props;

				if (pendingTagToSet.automationTemplateId && onPendingTagToSetHasAutomationTemplateId) {
					onPendingTagToSetHasAutomationTemplateId(pendingTagToSet);
				}

				this.setState(nextState, () => {
					if (onRequestAddTag) {
						onRequestAddTag(pendingTagToSet);
					}
				});
			};

			if (!pendingTag.id) {
				const { userSession } = this.props;
				const tagVM = new TagViewModel(userSession, { name: pendingTag.name });
				this.setState({ isAutocompleteOpen: false });

				tagVM
					.load()
					?.then(result => {
						if (result) {
							onFinish(result);
							return;
						}
						onFinish(pendingTag);
					})
					?.catch(() => {
						onFinish(pendingTag);
					});
			} else {
				onFinish(pendingTag);
			}
		} else {
			this.setState({
				pendingTag: pendingTag.name,
			});
		}
	};

	private onPendingTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const pendingTag = e.target.value;
		this.setState(
			{
				highlightedAutoCompleteSuggestionIndex: -1,
				isAutocompleteOpen: true,
				pendingTag,
			},
			() => {
				this.mAutoCompleteVm.setSearchQuery(pendingTag);
			}
		);
	};
}

const TagsEditorAsObserver = observer(_TagsEditor);
const TagsEditorWithContext = inject(UserSessionViewModelKey, ErrorMessagesViewModelKey)(TagsEditorAsObserver);
export const TagsList = withEventLogging(TagsEditorWithContext, 'TagsEditor');

// Component to handle automation functionality for tags
const TagWithAutomation: React.FC<{
	tagObject: IAccountTag;
	entity: EntityViewModel;
	disabled?: boolean;
	onRemoveButtonClicked: () => void;
	showRemoveButton: boolean;
	style?: React.CSSProperties;
	tagAlert?: TagAlertViewModel;
	showAutomationModalOnAdd?: boolean;
	onAutomationPopoverShown?: () => void;
}> = ({
	tagObject,
	entity,
	disabled,
	onRemoveButtonClicked,
	showRemoveButton,
	style,
	tagAlert,
	showAutomationModalOnAdd,
	onAutomationPopoverShown,
}) => {
	const { logApiError } = useEventLogging('TagWithAutomation');
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	// Fetch automation template if tag has automationTemplateId
	const { data: automationTemplate } = useAutomationTemplateQuery({
		id: tagObject.automationTemplateId,
		enabled: !!tagObject.automationTemplateId,
	});

	// Mutation to start automation for contact
	const startAutomationMutation = useStartAutomationForContactMutation({
		onSuccess: () => {
			// Reload the contact entity so inProgressAutomations updates,
			// which causes the ContactAutomationsProgressBanner to mount.
			// Then invalidate the automations query so the banner fetches fresh data.
			entity
				?.load?.()
				?.then(() => {
					invalidateAllContactAutomationsQueries();
				})
				.catch(error => {
					errorMessages.pushApiError(error);
					logApiError('ReloadContactAfterAutomation', error);
				});
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('StartAutomationFromTag', error);
		},
	});

	const handleStartAutomation = React.useCallback(
		(result?: ICreateAutomationRequest) => {
			if (!automationTemplate || !entity) return;

			// For automations started from tags, we always use the current entity's contact ID
			// The result parameter contains sendFromOptions if provided from the modal
			const contactId = entity.id;

			startAutomationMutation.mutate({
				template: automationTemplate as any,
				contactId,
				start: true,
				request: result,
			});
		},
		[automationTemplate, entity, startAutomationMutation]
	);

	// Create automation view model for Tag component compatibility
	// Include previewStartForContacts method for AutomationFromModal
	const automationVM = React.useMemo(() => {
		if (!automationTemplate) return null;

		return {
			...automationTemplate,
			isLoaded: true,
			load: () => Promise.resolve(),
			toJs: () => automationTemplate,
			previewStartForContacts: async (request: ICreateAutomationRequest | ICreateAutomationRequest[]) => {
				const requestBody = Array.isArray(request) ? request[0] : request;
				const urlPath = `Automation/${encodeURIComponent(automationTemplate.id)}/Contact/preview`;
				return userSession.webServiceHelper.callAsync(urlPath, 'POST', requestBody);
			},
		} as unknown as IAutomationTemplate;
	}, [automationTemplate, userSession]);

	const autoShowAutomationPopover = showAutomationModalOnAdd && !!automationTemplate;

	// Once both the flag is set AND the automation template has loaded, clear the flag
	React.useEffect(() => {
		if (showAutomationModalOnAdd && automationTemplate && onAutomationPopoverShown) {
			onAutomationPopoverShown();
		}
	}, [showAutomationModalOnAdd, automationTemplate, onAutomationPopoverShown]);

	return (
		<Tag
			automation={entity && automationVM}
			autoShowAutomationPopover={autoShowAutomationPopover}
			className={`${css(styleSheet.tagListItem)} truncate-text`}
			disabled={disabled}
			entity={entity}
			onRemoveButtonClicked={onRemoveButtonClicked}
			onStartAutomationButtonClicked={handleStartAutomation}
			showRemoveButton={showRemoveButton}
			style={style}
			tagAlert={tagAlert}
			tagValue={tagObject}
		/>
	);
};

interface TagsListWithTagsEditorProps {
	className?: string;
	disabled?: boolean;
	entity?: EntityViewModel;
	tagType?: ResourceAutoCompleteViewModelType.AccountTag | ResourceAutoCompleteViewModelType.Tag;
	onRequestAddTag(tag: IAccountTag): void;
	onRequestRemoveTag(tag: IAccountTag): void;
	tags: IAccountTag[];
	compactLayout?: boolean;
	isCompanyTags?: boolean;
	showInputEditor?: boolean;
}

export const TagsListWithTagsEditor = ({
	tags,
	entity,
	className,
	disabled,
	onRequestAddTag,
	onRequestRemoveTag,
	tagType,
	isCompanyTags,
	compactLayout,
	showInputEditor,
}: TagsListWithTagsEditorProps) => {
	const [tagsListMountedContentRect, setTagsListMountedContentRect] = React.useState<ContentRect | null>(null);
	const [showAllTags, setShowAllTags] = React.useState(false);
	const [newlyAddedTagWithAutomation, setNewlyAddedTagWithAutomation] = React.useState<string | null>(null);
	const userSession = useUserSession();

	const handleAutomationPopoverShown = () => {
		setNewlyAddedTagWithAutomation(null);
	};

	const handleTagWithAutomationAdded = React.useCallback((tag: IAccountTag) => {
		if (tag.automationTemplateId) {
			setNewlyAddedTagWithAutomation(tag.name);
		}
	}, []);

	const tagsWithAlerts = React.useMemo(
		() => (!!entity && getTypeForEntity(entity) !== 'company' ? (entity as IContact).tagsWithAlerts || [] : []),
		[entity]
	);

	const tagAlerts = React.useMemo(
		() =>
			tagsWithAlerts
				.map(x => new TagAlertViewModel(userSession, { tag: x }))
				.reduce<IDictionary<TagAlertViewModel>>((result, x) => {
					result[x.tagValue] = x;
					return result;
				}, {}),
		[tagsWithAlerts, userSession]
	);
	const showLimitTags = tags.length > MAX_TAGS;
	const tagsWithLimit = showLimitTags ? tags.slice(0, MAX_TAGS) : tags;
	const tagsToRender = showAllTags ? tags : tagsWithLimit;

	const onListResize = (contentRect: ContentRect) => {
		setTagsListMountedContentRect(contentRect);
	};

	const toggleTagsLimit = () => {
		setShowAllTags(showAll => !showAll);
	};

	/**
	 * Method checks if tag can be removed
	 *
	 * @param item { IAccountTag } the account tag object
	 * @returns {boolean} True if the tag is not from integration (can delete).
	 *
	 *   - If the tag is from integration and the integration is enabled block tag from being deleted.
	 */
	const canDeleteTag = (item: IAccountTag): boolean => {
		const hasIntegrationSource = entity?.tagsCollection?.find(x => x.name === item.name)?.source;
		// if tag has a source
		if (hasIntegrationSource) {
			// if the tag is enabled and is a source from the integration.
			return (
				!contactIsFromEnabledIntegration(entity as ContactViewModel, userSession.account.integrations) &&
				integrationSources.includes(hasIntegrationSource as IntegrationSources)
			);
		}
		return true;
	};

	return (
		<div className={`tags-editor ${className || ''}`}>
			<Measure client={true} onResize={onListResize}>
				{({ measureRef }) => {
					return (
						<>
							<div className={css(styleSheet.tagList)} ref={measureRef}>
								{tagsToRender.map(tagObject => {
									return (
										<TagWithAutomation
											key={tagObject.name}
											tagObject={tagObject}
											entity={entity}
											disabled={disabled}
											onRemoveButtonClicked={() => onRequestRemoveTag(tagObject)}
											showRemoveButton={canDeleteTag(tagObject)}
											showAutomationModalOnAdd={tagObject.name === newlyAddedTagWithAutomation}
											onAutomationPopoverShown={handleAutomationPopoverShown}
											style={
												tagsListMountedContentRect
													? {
															maxWidth: tagsListMountedContentRect.client.width,
														}
													: undefined
											}
											tagAlert={tagAlerts ? tagAlerts[tagObject.name] : undefined}
										/>
									);
								})}
								<TagsList
									onRequestAddTag={onRequestAddTag}
									onPendingTagToSetHasAutomationTemplateId={handleTagWithAutomationAdded}
									tagType={tagType}
									compactLayout={compactLayout}
									isCompanyTags={isCompanyTags}
									showInputEditor={showInputEditor}
								/>
							</div>
							{showLimitTags && (
								<button className={css(baseStyleSheet.brandLink)} onClick={toggleTagsLimit}>
									<span className={css(styleSheet.chevron)}>
										<DisclosureIcon
											type='chevron'
											className={css(styleSheet.chevronIcon, showAllTags && styleSheet.chevronFlip)}
											fillColor={brandPrimary}
										/>
									</span>
									<span>{showAllTags ? 'Hide Tags' : 'Show All Tags'}</span>
								</button>
							)}
						</>
					);
				}}
			</Measure>
		</div>
	);
};
